import { validators } from 'core/hooks/useForm'
import type { StaticFieldSchema } from 'core/utils/generated/formSchemas'

import type { SchemaField } from './schema'
import { isPhoneFieldName, toE164Us } from './phone'

type Rule = any

type RequiredOverrides = { [fieldPath: string]: boolean }

// An array's synthetic item node ('0') yields a path no form key ever matches,
// because flattenObject emits `field[0].x` rather than `field.0`.
const isArrayItemPath = (path: string) => path.includes('[')

const isPresent = (value: unknown): boolean =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && value.length === 0)

// `field._length` is how flattened state tracks an array's size. It holds 0 for
// an empty array, which would otherwise read as content and make a section that
// holds nothing but an empty array look occupied.
const isBookkeepingKey = (key: string) => key.endsWith('._length')

const sectionIsPresent = (values: unknown, sectionPath: string): boolean =>
  typeof values === 'object' &&
  values !== null &&
  Object.entries(values).some(
    ([key, value]) =>
      key.startsWith(`${sectionPath}.`) &&
      !isBookkeepingKey(key) &&
      isPresent(value),
  )

// A field the spec marks required inside a section the spec marks optional is
// required only once the section is in play. Demanding it while the section is
// empty blocks a record that legitimately omits the whole section.
const requiredWithinSection =
  (sectionPath: string) =>
  (value: unknown, formState?: { values?: unknown }): boolean | string =>
    sectionIsPresent(formState?.values, sectionPath)
      ? validators.required(value)
      : true

const appendRule = (
  rules: { [path: string]: Rule },
  path: string,
  rule: Rule,
): void => {
  const existing = rules[path]

  if (existing === undefined) {
    rules[path] = rule
    return
  }

  const asArray = (value: Rule) => (Array.isArray(value) ? value : [value])
  rules[path] = [...asArray(existing), ...asArray(rule)]
}

export const generateRequiredRules = (
  staticSchema?: Array<StaticFieldSchema>,
  requiredOverrides?: RequiredOverrides,
): { [path: string]: Rule } => {
  const rules: { [path: string]: Rule } = {}

  const walk = (
    fields: Array<StaticFieldSchema>,
    parentPath?: string,
    optionalSection?: string,
  ): void => {
    fields.forEach((field) => {
      const path =
        field.fullPath ??
        (parentPath ? `${parentPath}.${field.key}` : field.key)

      if (field.required && !isArrayItemPath(path)) {
        rules[path] = optionalSection
          ? requiredWithinSection(optionalSection)
          : validators.required
      }

      if (field.nested) {
        walk(field.nested, path, field.required ? optionalSection : path)
      }
    })
  }

  if (staticSchema) {
    walk(staticSchema)
  }

  Object.entries(requiredOverrides ?? {}).forEach(([path, isRequired]) => {
    if (isRequired) {
      rules[path] = validators.required
    } else {
      delete rules[path]
    }
  })

  return rules
}

export const generateValidationRules = (
  schema: Array<SchemaField>,
  options?: {
    staticSchema?: Array<StaticFieldSchema>
    requiredOverrides?: RequiredOverrides
  },
): { [path: string]: Rule } => {
  const rules = generateRequiredRules(
    options?.staticSchema,
    options?.requiredOverrides,
  )

  const processFields = (
    fields: Array<SchemaField>,
    parentPath?: string,
  ): void => {
    fields.forEach((field) => {
      const path = parentPath ? `${parentPath}.${field.key}` : field.key

      if (field.validation) {
        appendRule(rules, path, field.validation)
      }

      // Only leaf string fields whose path includes "phone"
      if (isPhoneFieldName(path) && field.type === 'string' && !field.nested) {
        appendRule(rules, path, (value: unknown) => {
          const str = (value ?? '').toString().trim()
          if (!str) return true
          return toE164Us(str) ? true : 'Please provide a valid phone number'
        })
      }

      if (field.nested) {
        processFields(field.nested, path)
      }
    })
  }

  processFields(schema)
  return rules
}
